import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar } from 'antd';
import {
  CloseOutlined,
  UserOutlined,
  PlusCircleOutlined,
  LogoutOutlined,
  SettingOutlined,
  HeartOutlined,
  DashboardOutlined,
  CarOutlined,
  CompassOutlined,
  ShoppingOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/useAuth';
import { useLogout } from '../../../hooks/useLogout';
import { AccordionSection } from './AccordionSection';
import { MenuItem } from './MenuItem';
import { vehicleManagementService } from '../../../api/services/vehicleManagementService';
import { listingsService } from '../../../api';
import './MobileMenu.css';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORIES = [
  { key: 'category_suv',       href: '/mysearch?bodyType=SUV',       img: '/images/categories/SUV.png' },
  { key: 'category_sedan',     href: '/mysearch?bodyType=SEDAN',     img: '/images/categories/Sedan.png' },
  { key: 'category_hatchback', href: '/mysearch?bodyType=HATCHBACK', img: '/images/categories/Hatchback.png' },
  { key: 'category_coupe',     href: '/mysearch?bodyType=COUPE',     img: '/images/categories/Coup.png' },
  { key: 'category_pickup',    href: '/mysearch?bodyType=PICKUP',    img: '/images/categories/Pickup.png' },
  { key: 'category_cabriolet', href: '/mysearch?bodyType=CABRIOLET', img: '/images/categories/Cabriolet.png' },
  { key: 'category_minivan',   href: '/mysearch?bodyType=MINIVAN',   img: '/images/categories/Minivan.png' },
];

function formatPrice(price: any, fallback: string): string {
  const n = Number(price);
  if (!price || isNaN(n)) return fallback;
  return `€ ${n.toLocaleString('de-DE')}`;
}

const PLACEHOLDER = '/images/placeholder.jpg';
const MAX_CARS = 5;

export const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { logout } = useLogout();
  
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [makes, setMakes] = useState<any[]>([]);
  const [makesLoading, setMakesLoading] = useState(true);
  const [cars, setCars] = useState<any[]>([]);
  const [carsLoading, setCarsLoading] = useState(true);

  useEffect(() => {
    if (!isOpen) return;

    if (makes.length === 0) {
      setMakesLoading(true);
      vehicleManagementService.getMakes()
        .then(res => setMakes(res.slice(0, 8))) // Take top 8
        .catch(() => setMakes([]))
        .finally(() => setMakesLoading(false));
    }

    if (cars.length === 0) {
      setCarsLoading(true);
      listingsService.getAll({ limit: MAX_CARS, page: 1 })
        .then(res => setCars(res?.listings?.slice(0, MAX_CARS) ?? []))
        .catch(() => setCars([]))
        .finally(() => setCarsLoading(false));
    }
  }, [isOpen, makes.length, cars.length]);

  const toggleSection = (section: string) => {
    setOpenSection(prev => (prev === section ? null : section));
  };

  const handleNavigate = (path: string) => {
    onClose();
    navigate(path);
  };

  const renderMakes = () => {
    if (makesLoading) return (
      <div className="mobile-skeleton-container">
        {[...Array(4)].map((_, i) => <div key={i} className="mobile-skeleton-item" />)}
      </div>
    );
    return makes.map(make => (
      <MenuItem 
        key={make.make}
        label={make.make}
        href={`/mysearch?make=${encodeURIComponent(make.make)}`}
        onNavigate={() => handleNavigate(`/mysearch?make=${encodeURIComponent(make.make)}`)}
      />
    ));
  };

  const renderCars = () => {
    if (carsLoading) return (
      <div className="mobile-skeleton-container">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="mobile-skeleton-item" style={{ height: '50px' }} />
        ))}
      </div>
    );
    return cars.map(car => {
      const thumb = car.thumbnailUrl || car.image || PLACEHOLDER;
      const name = car.title || `${car.year ?? ''} ${car.make ?? ''} ${car.model ?? ''}`.trim();
      const href = `/listing/${car.slug || car.id}`;
      return (
        <a 
          key={car.id} 
          className="mobile-menu-item"
          href="#" 
          onClick={e => { e.preventDefault(); handleNavigate(href); }}
        >
          <img src={thumb} alt={name} style={{ width: '40px', height: '30px', objectFit: 'cover', borderRadius: '4px' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '14px', fontWeight: 500, lineHeight: 1.1 }}>{name}</span>
            <span style={{ fontSize: '13px', color: 'var(--color1)', fontWeight: 600 }}>{formatPrice(car.price, t('home.price_on_request'))}</span>
          </div>
        </a>
      );
    });
  };

  return (
    <>
      <div 
        className={`mobile-menu-overlay ${isOpen ? 'open' : ''}`} 
        onClick={onClose} 
      />
      <div className={`mobile-menu-container ${isOpen ? 'open' : ''}`}>
        
        {/* Header */}
        <div className="mobile-menu-header">
          <a href="#" className="mobile-menu-logo" onClick={e => { e.preventDefault(); handleNavigate('/'); }}>
            <img src="/images/logos/AutoPlace.png" alt="AutoPlace Logo" />
          </a>
          <button className="mobile-menu-close" onClick={onClose} aria-label={t('nav.mobile.close_menu')}>
            <CloseOutlined />
          </button>
        </div>

        {/* User Info (If Logged In) */}
        {isAuthenticated && user && (
          <div className="mobile-user-info">
            <Avatar size={40} icon={<UserOutlined />} />
            <div className="mobile-user-details">
              <span className="mobile-user-name">{`${user.firstName} ${user.lastName}`}</span>
              <span className="mobile-user-role">{user.role || t('nav.user_default')}</span>
            </div>
          </div>
        )}

        {/* Menu Body - Accordions */}
        <div className="mobile-menu-body">
          <AccordionSection
            title={t('nav.mobile.buy')}
            icon={<CarOutlined />}
            isOpen={openSection === 'buy'}
            onToggle={() => toggleSection('buy')}
          >
            <div style={{ padding: '0 16px 8px', fontSize: '12px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('nav.buy_menu.browse_vehicles')}</div>
            <MenuItem label={t('nav.buy_menu.all_vehicles')} href="/mysearch" onNavigate={() => handleNavigate('/mysearch')} />
            <MenuItem label={t('nav.buy_menu.new_cars')} href="/mysearch?condition=NEW" onNavigate={() => handleNavigate('/mysearch?condition=NEW')} />
            <MenuItem label={t('nav.buy_menu.used_cars')} href="/mysearch?condition=USED" onNavigate={() => handleNavigate('/mysearch?condition=USED')} />

            <div style={{ padding: '16px 16px 8px', fontSize: '12px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('nav.buy_menu.popular_makes')}</div>
            {renderMakes()}

            <div style={{ padding: '16px 16px 8px', fontSize: '12px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('nav.buy_menu.categories')}</div>
            <div className="mobile-categories-grid">
              {CATEGORIES.map(c => {
                const label = t(`nav.buy_menu.${c.key}`);
                return (
                  <a key={c.href} href="#" className="mobile-category-item" onClick={e => { e.preventDefault(); handleNavigate(c.href); }}>
                    <img src={c.img} alt={label} className="mobile-category-img" />
                    {label}
                  </a>
                );
              })}
            </div>

            <div style={{ padding: '16px 16px 8px', fontSize: '12px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('nav.buy_menu.popular_cars')}</div>
            {renderCars()}
          </AccordionSection>

          <AccordionSection
            title={t('nav.mobile.sell')}
            icon={<ShoppingOutlined />}
            isOpen={openSection === 'sell'}
            onToggle={() => toggleSection('sell')}
          >
            <MenuItem label={t('nav.mobile.post_an_ad')} href={isAuthenticated ? '/dashboard/add-user-listing' : '/login'} onNavigate={() => handleNavigate(isAuthenticated ? '/dashboard/add-user-listing' : '/login')} />
            <MenuItem label={t('nav.my_advertisements')} href="/user-ads" onNavigate={() => handleNavigate('/user-ads')} />
            <MenuItem label={t('nav.mobile.pricing_packages')} href="/about-us" onNavigate={() => handleNavigate('/about-us')} />
          </AccordionSection>

          <AccordionSection
            title={t('nav.mobile.explore')}
            icon={<CompassOutlined />}
            isOpen={openSection === 'explore'}
            onToggle={() => toggleSection('explore')}
          >
            <MenuItem label={t('nav.mobile.featured_cars')} href="/mysearch" onNavigate={() => handleNavigate('/mysearch')} />
            <MenuItem label={t('nav.mobile.reviews')} href="/contactus" onNavigate={() => handleNavigate('/contactus')} />
            <MenuItem label={t('nav.mobile.about_us')} href="/about-us" onNavigate={() => handleNavigate('/about-us')} />
          </AccordionSection>

          <AccordionSection
            title={t('nav.mobile.account')}
            icon={<UserOutlined />}
            isOpen={openSection === 'account'}
            onToggle={() => toggleSection('account')}
          >
            {!isAuthenticated ? (
              <>
                <MenuItem label={t('nav.login')} href="/login" onNavigate={() => handleNavigate('/login')} />
                <MenuItem label={t('nav.register')} href="/register" onNavigate={() => handleNavigate('/register')} />
              </>
            ) : (
              <>
                <MenuItem label={t('nav.overview')} href="/dashboard/myvehicles" icon={<DashboardOutlined />} onNavigate={() => handleNavigate('/dashboard/myvehicles')} />
                <MenuItem label={t('nav.my_favorites')} href="/favorites" icon={<HeartOutlined />} onNavigate={() => handleNavigate('/favorites')} />
                <MenuItem label={t('nav.settings')} href="/settings" icon={<SettingOutlined />} onNavigate={() => handleNavigate('/settings')} />
                <button 
                  className="mobile-standalone-item danger" 
                  onClick={() => { logout(); onClose(); }}
                >
                  <LogoutOutlined className="mobile-standalone-icon" />
                  {t('nav.logout')}
                </button>
              </>
            )}
          </AccordionSection>
        </div>

        {/* Footer CTA */}
        <div className="mobile-menu-footer">
          <button className="mobile-cta-btn" onClick={() => handleNavigate(isAuthenticated ? '/dashboard/add-user-listing' : '/login')}>
            <PlusCircleOutlined />
            {t('nav.mobile.post_ad')}
          </button>
        </div>

      </div>
    </>
  );
};
